import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, UploadSimple } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ChequeList from './ChequeList';
import ChequeDialog from './ChequeDialog';
import ChequeImportDialog from './ChequeImportDialog';
import ChequeSummaryCards from './ChequeSummaryCards';
import { NC } from './chequesApi';
import { MonthField } from '@/components/ui/date-field';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_OPTIONS = [
  ['all', 'Todos os estados'], ['pedido_recebido', 'Pedido recebido'], ['registado_validado', 'Registado e validado'],
  ['consulta_realizada', 'Consulta realizada'], ['fatura_emitida', 'Fatura emitida'], ['reembolso_recebido', 'Reembolso recebido'], ['filipa_paga', 'Nutricionista paga'],
];

export default function NutriChequesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [cheques, setCheques] = useState([]);
  const [summary, setSummary] = useState(null);
  const [staff, setStaff] = useState([]);
  const [filters, setFilters] = useState({ status: 'all', nutri_id: 'all', month: '', edition: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([k, v]) => v && v !== 'all'));
    const [listRes, sumRes] = await Promise.all([
      axios.get(NC, { params }).catch(() => ({ data: [] })),
      axios.get(`${NC}/reports/summary`, { params: { month: filters.month, edition: filters.edition, nutri_id: filters.nutri_id === 'all' ? '' : filters.nutri_id } }).catch(() => ({ data: null })),
    ]);
    setCheques(listRes.data); setSummary(sumRes.data);
  }, [filters]);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { axios.get(`${API}/api/staff`).then(({ data }) => setStaff(data)).catch(() => {}); }, []);

  const openNew = () => { setEditId(null); setDialogOpen(true); };
  const openEdit = (id) => { setEditId(id); setDialogOpen(true); };

  return (
    <div className="fade-in" data-testid="nutri-cheques-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-heading font-medium text-xl text-brand-text">Cheques Nutricionista</h1>
        {isAdmin && (
          <div className="flex gap-2">
            <button onClick={() => setImportOpen(true)} className="text-xs text-brand-muted border border-brand-border rounded-full px-3 py-1.5 flex items-center gap-1.5" data-testid="cheque-open-import-btn"><UploadSimple size={13} /> Importar</button>
            <button onClick={openNew} className="text-xs bg-brand-green text-white rounded-full px-3 py-1.5 flex items-center gap-1.5" data-testid="cheque-open-new-btn"><Plus size={13} /> Novo cheque</button>
          </div>
        )}
      </div>
      <p className="text-xs text-brand-muted mb-4">Programa Cheque Nutricionista (Cuida-te, IPDJ), controlo desde o pedido até ao reembolso{isAdmin ? ' e ao pagamento à nutricionista.' : '.'}</p>

      <ChequeSummaryCards summary={summary} isAdmin={isAdmin} />

      <div className="flex flex-wrap gap-2 mb-4" data-testid="cheque-filters">
        <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v }))}>
          <SelectTrigger className="rounded-full border-brand-border h-8 text-xs w-auto" data-testid="cheque-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>{STATUS_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {isAdmin && (
          <Select value={filters.nutri_id} onValueChange={v => setFilters(f => ({ ...f, nutri_id: v }))}>
            <SelectTrigger className="rounded-full border-brand-border h-8 text-xs w-auto" data-testid="cheque-filter-nutri"><SelectValue placeholder="Nutricionista" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas</SelectItem>{staff.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <MonthField value={filters.month} onChange={e => setFilters(f => ({ ...f, month: e.target.value }))} className="rounded-full border border-brand-border h-8 text-xs px-3" data-testid="cheque-filter-month" />
        <input value={filters.edition} onChange={e => setFilters(f => ({ ...f, edition: e.target.value }))} placeholder="Edição (ex: 2026)" className="rounded-full border border-brand-border h-8 text-xs px-3 w-32" data-testid="cheque-filter-edition" />
      </div>

      <ChequeList cheques={cheques} isAdmin={isAdmin} onOpen={openEdit} />

      <ChequeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} chequeId={editId} isAdmin={isAdmin} staff={staff} onSaved={fetchAll} />
      {isAdmin && <ChequeImportDialog open={importOpen} onClose={() => setImportOpen(false)} staff={staff} onImported={fetchAll} />}
    </div>
  );
}
